import { runFingerprint, utf8Length } from './fingerprint.js';
/**
 * Action `run` (ADR-083) : port du bac à sable, contexte d'exécution version 1, limites et erreurs
 * typées. Rien ici n'exécute de code : l'implémentation QuickJS vit dans `./quickjs.ts` (sous-chemin
 * `@ayumia/component-runtime/quickjs`), pour que le navigateur ne charge l'interpréteur que si une
 * vue contient un `run`.
 *
 * Le code d'un `run` ne voit qu'un objet `context` **copié** (données JSON) : il calcule une valeur
 * et, au plus, écrit dans l'état local déclaré. Il ne peut appeler ni commande ni requête : une
 * mutation passe par une action `api` déclarée à la suite, autorisée au serveur.
 */
/** Versions du contexte d'exécution que ce runtime connaît (ADR-083 §2). */
export const RUN_CONTEXT_VERSIONS = new Set(['1']);
export const RUN_LIMITS = {
    /** Taille du code, en octets UTF-8. */
    code_bytes: 8 * 1024,
    default_timeout_ms: 50,
    max_timeout_ms: 200,
    /** Mémoire de l'interpréteur. */
    memory_bytes: 16 * 1024 * 1024,
    /** Pile de l'interpréteur : bornée sous celle de l'hôte, pour que la récursion échoue dans le bac à sable. */
    stack_bytes: 128 * 1024,
    /** Valeur rendue et écritures, sérialisées en JSON. */
    output_bytes: 64 * 1024,
};
export function runTimeout(action) {
    const requested = action.timeout_ms ?? RUN_LIMITS.default_timeout_ms;
    return Math.max(1, Math.min(requested, RUN_LIMITS.max_timeout_ms));
}
export function codeBytes(code) {
    return utf8Length(code);
}
export { runFingerprint };
/** Vérifie une valeur rendue contre `returns` ; `null` si conforme, sinon le chemin et le motif. */
export function checkReturns(schema, value, path = '') {
    const types = Array.isArray(schema.type) ? schema.type : [schema.type];
    const matches = (type) => {
        switch (type) {
            case 'string':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number' && Number.isFinite(value);
            case 'integer':
                return typeof value === 'number' && Number.isInteger(value);
            case 'boolean':
                return typeof value === 'boolean';
            case 'null':
                return value === null;
            case 'array':
                return Array.isArray(value);
            case 'object':
                return typeof value === 'object' && value !== null && !Array.isArray(value);
            default:
                return false;
        }
    };
    const matched = types.find(matches);
    if (matched === undefined)
        return `${path || '/'} : ${describe(value)} au lieu de ${types.join(' | ')}`;
    if (matched === 'array' && schema.items !== undefined) {
        const items = value;
        for (let index = 0; index < items.length; index += 1) {
            const problem = checkReturns(schema.items, items[index], `${path}/${index}`);
            if (problem !== null)
                return problem;
        }
    }
    if (matched === 'object') {
        const record = value;
        for (const name of schema.required ?? [])
            if (!Object.hasOwn(record, name))
                return `${path}/${name} : absent`;
        for (const [name, child] of Object.entries(schema.properties ?? {})) {
            if (!Object.hasOwn(record, name))
                continue;
            const problem = checkReturns(child, record[name], `${path}/${name}`);
            if (problem !== null)
                return problem;
        }
    }
    return null;
}
function describe(value) {
    if (value === null)
        return 'null';
    if (Array.isArray(value))
        return 'array';
    return typeof value;
}
const STATE_REFERENCE = /^\$?state\.([a-z][a-z0-9_]*)((?:\.[a-z0-9_]+)*)$/;
/**
 * Cible d'écriture d'un `run` : `$state.<nom>[.<chemin>]` d'un état déclaré, ou `null`. Vérifiée
 * par l'hôte après l'exécution, en plus du contrôle fait dans le bac à sable.
 */
export function runWriteTarget(target, stateNames) {
    const match = STATE_REFERENCE.exec(target);
    if (match === null || !stateNames.has(match[1]))
        return null;
    return [match[1], ...match[2].split('.').filter((segment) => segment !== '')];
}
export function collectRunActions(template) {
    const out = [];
    const visitActions = (actions, path) => {
        actions.forEach((entry, index) => {
            const at = `${path}/${index}`;
            const action = entry;
            if (action.action === 'run')
                out.push({ path: at, action: entry });
            if (Array.isArray(action.on_success))
                visitActions(action.on_success, `${at}/on_success`);
            if (Array.isArray(action.on_error))
                visitActions(action.on_error, `${at}/on_error`);
        });
    };
    const visit = (node, path) => {
        if (node === undefined || node === null || typeof node !== 'object')
            return;
        for (const [event, handler] of Object.entries(node.events ?? {}))
            if (Array.isArray(handler?.actions))
                visitActions(handler.actions, `${path}/events/${event}/actions`);
        (node.children ?? []).forEach((child, index) => visit(child, `${path}/children/${index}`));
    };
    visit(template, '/template');
    return out;
}
/** Empreinte du code de chaque `run` d'un manifeste, par chemin (audit de publication, ADR-083 §3). */
export function runFingerprints(manifest) {
    return collectRunActions(manifest.template).map(({ path, action }) => ({ path, fingerprint: runFingerprint(action.code) }));
}
export function hasRunAction(manifest) {
    return collectRunActions(manifest.template).length > 0;
}
//# sourceMappingURL=sandbox.js.map